import React, { useEffect, useState } from 'react';
import { Pencil, Star, ShieldCheck, ChevronRight, FileText, MessageCircle, AlertTriangle, LogOut, Crown, Award, Settings as SettingsIcon } from 'lucide-react';
import LogoutConfirmModal from '../../shared/LogoutConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Screen 9 — Profile & Settings.
 * Sections: identity → performance → safety → preferences → docs/support.
 */
const AccountTab = ({ user, onOpenProfile, onLogout }) => {
  const [scores, setScores] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [blocked, setBlocked] = useState([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    Promise.all([
      fetch(`${API}/api/driver/v2/${user.id}/scores`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/driver/v2/${user.id}/preferences`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/driver/v2/${user.id}/blocked-riders`).then(r => r.json()).catch(() => null),
    ]).then(([s, p, b]) => {
      if (cancelled) return;
      if (s?.success) setScores(s);
      if (p?.success) setPrefs(p.preferences);
      if (b?.success) setBlocked(b.items || []);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  const updatePref = async (patch) => {
    setPrefs((p) => ({ ...(p || {}), ...patch }));
    try {
      await fetch(`${API}/api/driver/v2/${user.id}/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
    } catch { /* ignore */ }
  };

  const unblock = async (riderId) => {
    setBlocked((b) => b.filter(x => x.rider_id !== riderId));
    try {
      await fetch(`${API}/api/driver/v2/${user.id}/blocked-riders/${riderId}`, { method: 'DELETE' });
    } catch { /* ignore */ }
  };

  return (
    <div className="absolute inset-0 pt-3 pb-24 overflow-y-auto bg-gray-50" data-testid="driver-account-tab-v2">
      <div className="px-4 pt-20 pb-4 space-y-3">
        {/* Identity card */}
        <div className="bg-white rounded-3xl p-4 shadow-sm" data-testid="account-identity">
          <div className="flex items-center gap-3">
            <div className="relative">
              {user?.profile_picture || user?.face_photo_url ? (
                <img src={user.profile_picture || user.face_photo_url} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-emerald-100" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg">
                  {(user?.firstName || user?.first_name || 'D')[0]?.toUpperCase()}
                </div>
              )}
              <button
                type="button"
                onClick={onOpenProfile}
                aria-label="Edit profile"
                className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center ring-2 ring-white"
                data-testid="account-edit-btn"
              >
                <Pencil className="w-3 h-3" />
              </button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[16px] font-bold text-gray-900 truncate">
                {(user?.firstName || user?.first_name || '')} {(user?.lastName || user?.last_name || '')}
              </p>
              <p className="text-[12px] text-gray-500 truncate">
                {user?.vehicle?.make ? `${user.vehicle.make} ${user.vehicle.model || ''}` : 'Vehicle not set'} {user?.vehicle?.plate ? `· ${user.vehicle.plate}` : ''}
              </p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                  scores?.tier === 'Gold' ? 'bg-amber-100 text-amber-700' : scores?.tier === 'Silver' ? 'bg-gray-200 text-gray-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  <Crown className="w-3 h-3" /> {scores?.tier || 'Standard'}
                </span>
                {scores?.avg_rating != null && (
                  <span className="inline-flex items-center gap-1 text-[12px] text-gray-700">
                    <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                    <span className="font-bold tabular-nums">{scores.avg_rating.toFixed(2)}</span>
                    <span className="text-[10px] text-gray-400">· last {Math.min(500, scores.rides_completed || 0)} trips</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Performance */}
        {scores && (
          <div className="bg-white rounded-3xl p-4 shadow-sm space-y-3" data-testid="account-performance">
            <p className="text-[13px] font-semibold text-gray-900">Performance</p>
            <ScoreBar
              testid="score-acceptance"
              label="Acceptance rate"
              pct={scores.acceptance_pct}
              valueText={`${scores.acceptance_pct}%`}
              tone={scores.acceptance_pct >= 90 ? 'emerald' : scores.acceptance_pct >= 70 ? 'amber' : 'rose'}
            />
            <ScoreBar
              testid="score-cancellation"
              label="Cancellation rate"
              pct={scores.cancellation_pct}
              valueText={`${scores.cancellation_pct}%`}
              tone={scores.cancellation_pct <= 5 ? 'emerald' : scores.cancellation_pct <= 12 ? 'amber' : 'rose'}
              invert
            />
            <ScoreBar
              testid="score-behavior"
              label="Driving behavior"
              pct={scores.behavior_score}
              valueText={`${scores.behavior_score}/100`}
              tone={scores.behavior_score >= 90 ? 'emerald' : scores.behavior_score >= 75 ? 'amber' : 'rose'}
            />
          </div>
        )}

        {/* Safety */}
        <Section title="Safety" testid="account-safety">
          <SettingRow
            testid="setting-blocked-riders"
            label={`Blocked riders (${blocked.length})`}
            sub={blocked.length === 0 ? 'You haven\u2019t blocked anyone' : 'Tap to view & unblock'}
            onClick={() => {
              if (blocked.length === 0) return;
              const choice = prompt(`Blocked riders:\n${blocked.map((b, i) => `${i + 1}. ${b.rider_name || b.rider_id}`).join('\n')}\n\nEnter a number to unblock (or cancel):`);
              if (!choice) return;
              const idx = parseInt(choice, 10) - 1;
              if (blocked[idx]) unblock(blocked[idx].rider_id);
            }}
          />
          <SettingRow
            testid="setting-emergency-contacts"
            label="Emergency contacts"
            sub="Set who gets notified when you press SOS"
            onClick={() => alert('Emergency contacts coming with the rider Safety wiring (P1 backlog).')}
          />
          <SettingToggle
            testid="setting-women-mode"
            label="Women-only ride mode"
            checked={!!prefs?.women_only_mode}
            onChange={(v) => updatePref({ women_only_mode: v })}
          />
          <SettingToggle
            testid="setting-fatigue"
            label="Fatigue reminders"
            checked={prefs?.fatigue_alert_enabled !== false}
            onChange={(v) => updatePref({ fatigue_alert_enabled: v })}
          />
          {prefs?.fatigue_alert_enabled !== false && (
            <div className="px-4 py-3 border-t border-gray-100" data-testid="setting-fatigue-threshold">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                Alert me after (hours)
              </label>
              <div className="flex gap-1.5">
                {[3, 4, 5, 6].map(h => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => updatePref({ fatigue_threshold_hours: h })}
                    className={`flex-1 h-9 rounded-lg text-[12px] font-semibold tabular-nums ${
                      prefs?.fatigue_threshold_hours === h ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                    data-testid={`fatigue-threshold-${h}`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Preferences */}
        <Section title="Preferences" testid="account-preferences">
          <SettingChoice
            testid="setting-nav"
            label="Navigation app"
            options={[{ id: 'google', label: 'Google Maps' }, { id: 'in_app', label: 'In-app' }]}
            value={prefs?.nav_app || 'google'}
            onChange={(v) => updatePref({ nav_app: v })}
          />
          <SettingChoice
            testid="setting-language"
            label="Language"
            options={[{ id: 'en', label: 'English' }, { id: 'ny', label: 'Chichewa' }]}
            value={prefs?.language || 'en'}
            onChange={(v) => updatePref({ language: v })}
          />
          <SettingToggle
            testid="setting-notif-bonuses"
            label="Bonus notifications"
            checked={prefs?.notifications?.bonuses !== false}
            onChange={(v) => updatePref({ notifications: { ...(prefs?.notifications || {}), bonuses: v } })}
          />
          <SettingToggle
            testid="setting-notif-support"
            label="Support messages"
            checked={prefs?.notifications?.support !== false}
            onChange={(v) => updatePref({ notifications: { ...(prefs?.notifications || {}), support: v } })}
          />
        </Section>

        {/* Docs & support */}
        <Section title="Documents & support" testid="account-docs">
          <SettingRow
            testid="setting-documents"
            label="Documents"
            sub={user?.verificationStatus === 'verified' ? '✓ All documents verified' : 'Open profile to manage'}
            icon={<FileText className="w-4 h-4 text-gray-400" />}
            onClick={onOpenProfile}
          />
          <SettingRow
            testid="setting-support"
            label="Chat with support"
            sub="Live chat · 24/7"
            icon={<MessageCircle className="w-4 h-4 text-emerald-600" />}
            onClick={() => alert('Support chat will be wired up in a follow-up — for now, email support@yachangu.com')}
          />
          <SettingRow
            testid="setting-incident"
            label="Report a safety incident"
            sub="Dedicated flow"
            icon={<AlertTriangle className="w-4 h-4 text-rose-500" />}
            onClick={() => alert('Safety-incident flow lands with the Safety backend wiring.')}
          />
        </Section>

        {/* Logout */}
        <button
          type="button"
          onClick={() => setShowLogoutConfirm(true)}
          data-testid="account-logout"
          className="w-full h-12 mt-2 rounded-2xl bg-white text-rose-600 font-semibold inline-flex items-center justify-center gap-2 border border-rose-100"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <LogoutConfirmModal
        open={showLogoutConfirm}
        onCancel={() => setShowLogoutConfirm(false)}
        onConfirm={() => { setShowLogoutConfirm(false); onLogout?.(); }}
      />
    </div>
  );
};

const Section = ({ title, testid, children }) => (
  <div className="bg-white rounded-3xl shadow-sm overflow-hidden" data-testid={testid}>
    <p className="text-[12px] font-semibold uppercase tracking-wider text-gray-500 px-4 pt-3 pb-1">{title}</p>
    <div className="divide-y divide-gray-100">{children}</div>
  </div>
);

const SettingRow = ({ label, sub, icon, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
  >
    {icon ? <div className="shrink-0">{icon}</div> : null}
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-semibold text-gray-900">{label}</p>
      {sub && <p className="text-[11px] text-gray-500 truncate">{sub}</p>}
    </div>
    <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
  </button>
);

const SettingToggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50" data-testid={testid}>
    <span className="flex-1 text-[13px] font-semibold text-gray-900">{label}</span>
    <span
      className={`relative w-10 h-6 rounded-full transition-colors ${checked ? 'bg-emerald-600' : 'bg-gray-300'}`}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`}
      />
    </span>
    <input type="checkbox" className="sr-only" checked={!!checked} onChange={(e) => onChange?.(e.target.checked)} />
  </label>
);

const SettingChoice = ({ label, options, value, onChange, testid }) => (
  <div className="px-4 py-3" data-testid={testid}>
    <p className="text-[13px] font-semibold text-gray-900 mb-2">{label}</p>
    <div className="flex gap-1.5">
      {options.map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange?.(o.id)}
          className={`flex-1 h-9 rounded-lg text-[12px] font-semibold ${
            value === o.id ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const ScoreBar = ({ label, pct, valueText, tone = 'emerald', invert = false, testid }) => {
  const colors = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
  };
  // For "invert" (lower is better), display the bar inverted
  const fill = invert ? Math.min(100, 100 - pct) : Math.min(100, pct);
  return (
    <div data-testid={testid}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-[12px] text-gray-700">{label}</span>
        <span className="text-[12px] font-bold text-gray-900 tabular-nums">{valueText}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${colors[tone]} transition-[width] duration-700`} style={{ width: `${fill}%` }} />
      </div>
    </div>
  );
};

export default AccountTab;
